import { Popover } from '@nextui-org/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { BackArrowFull, ConnectionsIcon, SupportIcon } from '../../../../../../assets';
import { useAuthContext } from '../../../../../../contexts/AuthContext';
import {
  NavigationItem,
  SystemNavigationItemType,
  useNavigationContext,
} from '../../../../../../contexts/NavigationContext';
import { useNotificationContext } from '../../../../../../contexts/NotificationContext';
import { useSessionContext } from '../../../../../../contexts/SessionContext';
import { useToasterContext } from '../../../../../../contexts/ToasterContext';
import { useAppContext } from '../../../../../../hooks/AppContext';
import useClickOutsideAlerter from '../../../../../../hooks/useClickOutsideAlerter';
import { inCustomBreakpoint, useWindowDimensions } from '../../../../../../hooks/useDimensions';
import { useIsOverflow } from '../../../../../../hooks/useIsOverflow';
import { ImgProxyResizer } from '../../../../../../utils/ImageProxyResizer';
import { getFullName, getInitials } from '../../../../../../utils/User';
import NotificationsSection, {
  ScrollReference,
} from '../../../../../../views/notifications/NotificationsPage/NotificationsSection';
import ProfilePopover from '../../../../../../views/settings/ProfilePopover';
import Modal from '../../../../../Modal';
import TextTooltip from '../../../../../TextTooltip';
import { MIN_WIDTH_EXPANDABLE } from '../../../BaseDrawerLayout/BaseDrawerLayout.styles';
import { useDrawerLayoutContext } from '../../../BaseDrawerLayout/context';
import NavigationButton from '../NavigationButton';
import {
  AnimationContainer,
  AppDrawerContentContainer,
  AppDrawerContentOverlay,
  AvatarContainer,
  BackButton,
  BackButtonContainer,
  Badge,
  Bottom,
  BrandContainer,
  Logo,
  LogoutModalContainer,
  MenuContainer,
  MoreBubble,
  NavigationItems,
  NavigationItemsContainer,
  NotificationIconContainer,
  NotificationsContainer,
  NotificationsContainerProps,
  NotificationsExitIcon,
  NotificationsGoBackContainer,
  NotificationsHeader,
  NotificationsHeaderLeftContent,
  NotificationsHeaderRightContent,
  NotificationsHeaderTitle,
  StyledArrowDownIcon,
  StyledAvatar,
  StyledBellIcon,
  StyledCarIcon,
  StyledOrganizationsIcon,
  StyledPopoverContent,
  StyledQuickValuationIcon,
  SystemItemsContainer,
  Top,
  UserName,
} from './AppDrawerContent.styles';

interface DrawerNotificationsProps {
  display: boolean; //showNotifications
  position: NotificationsContainerProps['$position'];
  onGoBack: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const DrawerNotifications = ({ display, position, onGoBack }: DrawerNotificationsProps) => {
  const { t } = useTranslation();
  const [isShowHeaderBottomBorder, setShowHeaderBottomBorder] = useState(false);
  const notificationsContainerRef = useRef(null);
  const clickedOutside = useClickOutsideAlerter(notificationsContainerRef, display);

  useEffect(() => {
    if (clickedOutside) {
      onGoBack?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedOutside]);

  useEffect(() => {
    const notificationList = document.getElementById(ScrollReference.NotificationList);
    const handleScroll = () => setShowHeaderBottomBorder(notificationList.scrollTop > 0);

    if (display) {
      notificationList?.addEventListener?.('scroll', handleScroll);
    } else {
      notificationList?.removeEventListener?.('scroll', handleScroll);
    }
  }, [display]);

  // Partials
  const Header = () => {
    return (
      <NotificationsHeader $showBottomBorder={isShowHeaderBottomBorder}>
        <NotificationsHeaderLeftContent>
          <NotificationsHeaderTitle>{t('notifications.title')}</NotificationsHeaderTitle>
        </NotificationsHeaderLeftContent>
        <NotificationsHeaderRightContent>
          <NotificationsGoBackContainer>
            <NotificationsExitIcon onClick={onGoBack} />
          </NotificationsGoBackContainer>
        </NotificationsHeaderRightContent>
      </NotificationsHeader>
    );
  };

  return (
    <NotificationsContainer $display={display} $position={position} ref={notificationsContainerRef}>
      <NotificationsSection header={<Header />} scrollReference={ScrollReference.NotificationList} />
    </NotificationsContainer>
  );
};

const AppDrawerContent = () => {
  // Dependencies
  const { me } = useSessionContext();
  const { t } = useTranslation();
  const { logout } = useAuthContext();
  const { navigationContent } = useNavigationContext();
  const { unreadCount, resetUnreadCount } = useNotificationContext();
  const { isOpened: isDrawerOpened, setDisplayToggle } = useDrawerLayoutContext();
  const { setTawkMessengerVisible } = useAppContext();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const TopContainerRef = React.useRef();
  const DrawerContainerRef = React.useRef<HTMLDivElement>();
  const isTopContainerOverflow = useIsOverflow(TopContainerRef);
  const location = useLocation();
  const { setToasterNotificationsIsOpen } = useToasterContext();

  // State
  const [isPopoverOpen, setPopoverOpen] = React.useState(false);
  const [displayDropdown, setDisplayDropdown] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);

  const isOpened = inCustomBreakpoint(MIN_WIDTH_EXPANDABLE, width) && isDrawerOpened;

  // Derived State
  const initials = getInitials(me?.firstName, me?.lastName);

  useEffect(() => {
    setToasterNotificationsIsOpen(showNotifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotifications]);

  const handleLogout = () => {
    setPopoverOpen(false);
    // We need to wait for the popover to close before opening the modal
    setTimeout(() => setIsLogoutModalOpen(true), 100);
  };

  const handleNotificationsOnClick = (e: any) => {
    e.preventDefault();
    resetUnreadCount();
    setShowNotifications(true);
    setDisplayToggle(false);
  };

  const resizedAvatarUrl = new ImgProxyResizer(me?.imageUrl).largeAvatarSquaredUrl;

  /**
   * Given a children and a textKey returns a component including the wrapping the children inside a Tooltip .
   * We want to display this tooltip only when the drawer is closed/collapsed
   * so if it is open we just return the same children
   * @param children Component to be included inside the tooltip
   * @param textKey i18n key to be translated and used as the tooltip text
   * @returns new component with the Tooltip
   */
  const wrapInTooltip = (children: React.ReactElement, text: string) => {
    return isOpened ? (
      children
    ) : (
      <TextTooltip tooltipText={text} placement="right">
        {children}
      </TextTooltip>
    );
  };

  const onBackToListingClick = () => {
    navigate('/app/listings');
  };

  const handleButtonClick = (item: NavigationItem) => {
    if (item.navigateHash) {
      window.location.hash = item.navigateHash;
    } else {
      navigate(item.navigatePath);
    }
  };

  const mainItems = (
    <AnimationContainer>
      <NavigationButton
        onClick={onBackToListingClick}
        icon={wrapInTooltip(<StyledCarIcon />, t('listings.title'))}
        isActive={!showNotifications && location.pathname === '/app/listings'}
      >
        {t('listings.title')}
      </NavigationButton>
      {process.env.REACT_APP_QUICK_VALUATION_FEATURE_FLAG === 'true' && (
        <NavigationButton
          onClick={() => navigate('/app/quickValuation')}
          icon={wrapInTooltip(<StyledQuickValuationIcon />, t('createVehicle.quickValuation'))}
          isActive={!showNotifications && location.pathname === '/app/quickValuation'}
        >
          {t('createVehicle.quickValuation')}
        </NavigationButton>
      )}
    </AnimationContainer>
  );

  const customItems = useMemo(
    () => (
      <AnimationContainer>
        {navigationContent?.backItem && (
          <BackButtonContainer
            $drawerIsOpen={isOpened}
            onClick={() => (navigationContent.backItem.onBack ? navigationContent.backItem.onBack() : navigate(-1))}
          >
            <BackButton $drawerIsOpen={isOpened}>
              <BackArrowFull />
            </BackButton>
            {isOpened && <span>{navigationContent.backItem.title}</span>}
          </BackButtonContainer>
        )}
        <MenuContainer>
          {navigationContent?.items?.map((item) => (
            <NavigationButton
              onClick={() => handleButtonClick(item)}
              icon={wrapInTooltip(item.icon, item.title)}
              isActive={
                !showNotifications &&
                (item.navigateHash
                  ? location.hash === `#${item.navigateHash}`
                  : location.pathname === item.navigatePath)
              }
            >
              {t(item.title)}
            </NavigationButton>
          ))}
        </MenuContainer>
      </AnimationContainer>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location, isOpened, showNotifications, navigationContent],
  );

  const systemState = (
    <>
      <NavigationButton
        icon={wrapInTooltip(
          <NotificationIconContainer>
            <StyledBellIcon />
            {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
          </NotificationIconContainer>,
          t('notifications.title'),
        )}
        onClick={handleNotificationsOnClick}
        isActive={showNotifications}
      >
        {t('notifications.title')}
      </NavigationButton>
      <NavigationButton
        onClick={() => navigate('/app/connections')}
        icon={wrapInTooltip(<ConnectionsIcon />, t('connections.title'))}
        isActive={!showNotifications && location.pathname === '/app/connections'}
      >
        {t('connections.title')}
      </NavigationButton>
      {!navigationContent?.hiddenSystemItems?.includes(SystemNavigationItemType.Organizations) && (
        <NavigationButton
          onClick={() => navigate('/app/organizations')}
          icon={wrapInTooltip(<StyledOrganizationsIcon />, t('organizations.title'))}
          isActive={!showNotifications && location.pathname === '/app/organizations'}
        >
          {t('organizations.title')}
        </NavigationButton>
      )}
      {process.env.REACT_APP_TAWK_MESSENGER_ENABLED === 'true' &&
        !navigationContent?.hiddenSystemItems?.includes(SystemNavigationItemType.Support) && (
          <NavigationButton
            onClick={() => setTawkMessengerVisible(true)}
            icon={wrapInTooltip(<SupportIcon />, t('common.support'))}
            isActive={false}
          >
            {t('common.support')}
          </NavigationButton>
        )}
      <Popover isOpen={isPopoverOpen} onOpenChange={setPopoverOpen} placement="top-left">
        <Popover.Trigger>
          <AvatarContainer
            $drawerIsOpen={isOpened}
            $isActive={isPopoverOpen || (!showNotifications && location.pathname?.includes('/app/settings'))}
          >
            <StyledAvatar pointer text={initials} size="sm" src={resizedAvatarUrl} />
            <UserName $drawerIsOpen={isOpened}>{getFullName(me?.firstName, me?.lastName)}</UserName>
          </AvatarContainer>
        </Popover.Trigger>
        <StyledPopoverContent>
          <ProfilePopover onClose={() => setPopoverOpen(false)} onLogout={handleLogout} />
        </StyledPopoverContent>
      </Popover>
    </>
  );

  const scrollTimerRef = React.useRef<any>();

  React.useEffect(() => {
    setDisplayDropdown(false);
  }, [isOpened]);

  React.useEffect(() => {
    const itemsHeight = 40;
    const topView = document.getElementById('navigationsItemsContainer');
    const bubble = document.getElementById('bubble');
    bubble.style.visibility = 'hidden';

    const scrollEventFnc = () => {
      const bubble = document.getElementById('bubble');
      const shouldShowBubble = topView?.scrollTop < topView.scrollHeight - topView.offsetHeight - itemsHeight;
      // The setTimeOut will be executed after 200 miliseconds the user stops scrolling to hide the "More" bubble
      if (scrollTimerRef.current !== null) {
        clearTimeout(scrollTimerRef.current);
      }
      scrollTimerRef.current = null;
      if (shouldShowBubble) {
        bubble.style.visibility = 'visible';
        scrollTimerRef.current = setTimeout(function () {
          bubble.style.visibility = 'hidden';
        }, 200);
      } else {
        bubble.style.visibility = 'hidden';
      }
    };
    topView?.addEventListener?.('scroll', scrollEventFnc);
    return () => topView?.removeEventListener?.('scroll', scrollEventFnc);
  }, []);

  const handleTransitionEnd = (event) => {
    if (event.propertyName !== 'width') return;
    setDisplayDropdown(isOpened === true);
  };

  const getNotificationsPosition = (): NotificationsContainerProps['$position'] => {
    if (DrawerContainerRef.current) {
      return {
        left: DrawerContainerRef.current?.offsetWidth,
      };
    }

    return {
      left: 0,
    };
  };

  return (
    <>
      <AppDrawerContentContainer
        ref={DrawerContainerRef}
        $drawerIsOpen={isOpened}
        onTransitionEnd={handleTransitionEnd}
      >
        <Top $drawerIsOpen={isOpened}>
          <BrandContainer $drawerIsOpen={isOpened}>
            <Logo $drawerIsOpen={isOpened} />
          </BrandContainer>
          <NavigationItemsContainer
            id="navigationsItemsContainer"
            ref={TopContainerRef}
            $addBorderBottom={isTopContainerOverflow && isOpened && displayDropdown}
            $drawerIsOpen={isOpened}
          >
            <NavigationItems>
              {navigationContent ? customItems : mainItems}
              <MoreBubble id="bubble" $shouldDisplay={isOpened && isTopContainerOverflow} ref={scrollTimerRef}>
                <StyledArrowDownIcon />
                {t('common.more')}
              </MoreBubble>
            </NavigationItems>
          </NavigationItemsContainer>
        </Top>
        <Bottom>
          <SystemItemsContainer>{systemState}</SystemItemsContainer>
        </Bottom>
      </AppDrawerContentContainer>
      <AppDrawerContentOverlay></AppDrawerContentOverlay>
      <DrawerNotifications
        display={showNotifications}
        position={getNotificationsPosition()}
        onGoBack={(event) => {
          setShowNotifications(false);
          setDisplayToggle(false);
          event?.stopPropagation();
        }}
      />
      <Modal
        title={t('profile.logout')}
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onCancel={() => setIsLogoutModalOpen(false)}
        onSubmit={() => void logout()}
        cancelButtonTitle={t('common.cancel')}
        submitButtonTitle={t('profile.confirmLogout')}
      >
        <LogoutModalContainer>{t('profile.confirmLogoutMessage')}</LogoutModalContainer>
      </Modal>
    </>
  );
};

export default AppDrawerContent;
